/**
 * Display metadata for the Acquisition Toolbox — the workstream headings and
 * phase tags. Item content lives in the database (seed 007).
 *
 * REBRANDED FROM THE SOURCE. The original tagged phases with a traffic-light
 * ramp — red, amber, yellow, green. Brand Direct has no green and no amber, so
 * the same ORDERING is expressed in the brand's own severity ramp: crimson for
 * the work that cannot wait, cooling through to navy for the work that has a
 * year to happen. The meaning survives; the palette is this app's.
 */
export interface PhaseMeta {
  key: string;
  label: string;
  /** A CSS variable, never a hex — see the note in app.css. */
  token: string;
}

/* Tuple rather than PhaseMeta[]: with noUncheckedIndexedAccess on, indexing an
   array yields `T | undefined`, and MA_PHASES[0] is a value we know exists. */
export const MA_PHASES = [
  { key: 'day1', label: 'Day 1 / Week 1', token: 'var(--accent-500)' },
  { key: 'd30', label: 'First 30 Days', token: 'var(--accent-300)' },
  { key: 'd90', label: 'Days 31–90', token: 'var(--brand-400)' },
  { key: 'y1', label: 'Months 4–12', token: 'var(--brand-600)' },
] as const satisfies readonly PhaseMeta[];

export interface WorkstreamMeta {
  key: string;
  name: string;
  blurb: string;
}

export const MA_WORKSTREAMS: WorkstreamMeta[] = [
  {
    key: 'governance',
    name: 'Integration Governance',
    blurb: 'Someone has to own the integration or everything below slips.',
  },
  {
    key: 'people',
    name: 'People & Culture',
    blurb: 'Employee flight is the fastest way to lose the client base you just paid for.',
  },
  {
    key: 'clients',
    name: 'Clients & Revenue Retention',
    blurb: 'The valuation assumed the clients stay. Make that true.',
  },
  {
    key: 'technology',
    name: 'Technology & Tooling',
    blurb: 'Two of everything is a cost problem; half-migrated is a risk problem.',
  },
  {
    key: 'service',
    name: 'Service Delivery & Operations',
    blurb: 'Clients judge the acquisition by whether tickets still get fixed.',
  },
  {
    key: 'finance',
    name: 'Finance, Legal & Admin',
    blurb: 'The unglamorous items that turn into expensive surprises when skipped.',
  },
  {
    key: 'sales',
    name: 'Sales, Marketing & Brand',
    blurb: 'The market is watching how you handle this. So are the next sellers.',
  },
];

export const phaseInfo = (key: string): PhaseMeta =>
  MA_PHASES.find((p) => p.key === key) ?? MA_PHASES[0];

/** The default phase for a new item — the first, most urgent one. */
export const DEFAULT_PHASE: string = MA_PHASES[0].key;

export interface MaToolboxItem {
  MaToolboxItemId: string;
  Workstream: string;
  Title: string;
  Detail: string;
  Phase: string;
  Checked: boolean;
  IsCustom: boolean;
  SortOrder: number;
}
